import { useLeaderApproval } from "@/queries/formality-request";
import { useToast } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useCorrectionForm } from "../context";
import axios from "axios";

export type LeaderModalState = "idle" | "reject" | "approve";

/**
 * Approve/reject actions a group leader performs over the current cycle's
 * correction, together with the state of the confirmation modals.
 */
export const useLeaderApprovalActions = (procedureId: string) => {
  const [leaderModalState, setLeaderModalState] =
    useState<LeaderModalState>("idle");
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { rejectComments, setRejectComments, groupCorrectionInCurrentCycle } =
    useCorrectionForm();

  const { mutateAsync: leaderApproval, isPending: isLoadingLeaderApproval } =
    useLeaderApproval();

  const handleLeaderReject = async () => {
    if (
      !groupCorrectionInCurrentCycle ||
      !rejectComments.trim() ||
      rejectComments.trim().length > 1000
    )
      return;

    try {
      await leaderApproval({
        correction_id: groupCorrectionInCurrentCycle?.id,
        approved: false,
        comments: rejectComments,
      });

      showToast({
        title: "Comentarios rechazados",
        description: "Los comentarios han sido rechazados y eliminados",
        options: {
          type: "error",
        },
      });

      setLeaderModalState("idle");
      setRejectComments("");
      navigate(`/tramite/${procedureId}`);
    } catch (error) {
      const defaultMessage = "Ocurrió un error al rechazar los comentarios";
      if (axios.isAxiosError(error)) {
        showToast({
          title: "Error al rechazar",
          description: error?.response?.data?.message
            ? error?.response?.data?.message
            : defaultMessage,
          options: {
            type: "error",
          },
        });
        return;
      }
      showToast({
        title: "Error al rechazar",
        description: error instanceof Error ? error.message : defaultMessage,
        options: {
          type: "error",
        },
      });
    }
  };

  const handleLeaderApprove = async () => {
    if (!groupCorrectionInCurrentCycle) return;

    try {
      await leaderApproval({
        correction_id: groupCorrectionInCurrentCycle?.id,
        approved: true,
      });

      showToast({
        title: "Comentarios aprobados",
        description: `Estos comentarios han sido enviados enviados al usuario correspondiente`,
        options: {
          type: "success",
        },
      });

      setLeaderModalState("idle");
      navigate(`/tramite/${procedureId}`);
    } catch {
      showToast({
        title: "Error al aprobar",
        description: "Ocurrió un error al aprobar los comentarios",
        options: {
          type: "error",
        },
      });
    }
  };

  return {
    leaderModalState,
    setLeaderModalState,
    isLoadingLeaderApproval,
    handleLeaderReject,
    handleLeaderApprove,
  };
};
